import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Activity } from 'lucide-react';
import therappiWhoopLogo from '@/assets/therappi-whoop-combined.svg';

export default function WhoopLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Redirect authenticated non-demo users to settings integrations tab
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user && user.email?.toLowerCase() !== 'support@therappi.app') {
        // Authenticated non-demo user - redirect to settings integrations
        navigate('/settings?tab=integrations', { replace: true });
      } else {
        setCheckingAuth(false);
      }
    };
    checkAuth();
  }, [navigate, searchParams]);

  // WhoopLogin component mounted

  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/50 to-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        setError(error.message);
        return;
      }

      // Verify this is the demo user
      if (data?.user?.email?.toLowerCase() !== 'support@therappi.app') {
        setError('This login page is only for WHOOP demo access. Please use the main login page.');
        await supabase.auth.signOut();
        return;
      }

      // Success - navigate to WHOOP dashboard using React Router
      navigate('/whoop-dashboard');
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/50 to-background p-4">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center mb-2">
            <img 
              src={therappiWhoopLogo} 
              alt="Therappi × WHOOP" 
              className="h-24 object-contain"
            />
          </div>
          <div className="flex items-center justify-center">
            <Activity className="h-8 w-8 text-primary mr-2" />
            <CardTitle className="text-2xl">WHOOP Demo Access</CardTitle>
          </div>
          <CardDescription className="text-base">
            Sign in to explore the Therappi × WHOOP integration demo
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSignIn} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="support@therappi.app"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter password"
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              size="lg"
            >
              {loading ? 'Signing in...' : 'Access WHOOP Demo'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            <p>This is a demo environment showcasing the</p>
            <p className="font-semibold">Therappi × WHOOP Integration</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
